import React from "react";
import {
  EAutoExposureMethod,
  VisibleCaptureProperty,
  VisibleFluorescenceCaptureProperty,
} from "../../../Model/CaptureProfile";
import DefaultImagingSettingRadioButton from "./DefaultImagingSettingRadioButton";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import "./ExposureOptions.css";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import Row from "./Row";

type Props = {
  exposureCaptureProperty:
    | VisibleCaptureProperty
    | VisibleFluorescenceCaptureProperty;
  style: React.CSSProperties;
  lightpathCapability: LightPathCapability;
  onChange: (
    exposureCaptureProperty:
      | VisibleCaptureProperty
      | VisibleFluorescenceCaptureProperty
  ) => void;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function ExposureOptions({
  style,
  exposureCaptureProperty,
  lightpathCapability,
  onChange,
  onErrorCleared,
  onErrorOccur,
}: Props) {
  const AUTO_EXPOSURE_METHOD = "autoExposureMethod";
  const autoExposureAlgorithmProperty = exposureCaptureProperty.autoExposure;
  if (autoExposureAlgorithmProperty == null) {
    throw new Error("Auto exposure algorithm property is null");
  }

  if (exposureCaptureProperty.exposure == null) {
    throw new Error("Capture profile exposure is null");
  }
  if (exposureCaptureProperty.brightness == null) {
    throw new Error("Capture profile brightness is null");
  }
  if (exposureCaptureProperty.gain == null) {
    throw new Error("Capture profile gain is null");
  }
  if (exposureCaptureProperty.gamma == null) {
    throw new Error("Capture profile gamma is null");
  }

  if (lightpathCapability.camera.exposure == null) {
    throw new Error(
      `LightPath ${lightpathCapability.lightpath} doesn't support exposure`
    );
  }

  return (
    <div
      className="exposure-setting"
      style={{
        ...style,
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          height: "57px",
          display: "flex",
          alignItems: "center",
          fontStyle: "normal",
          fontSize: 13,
          lineHeight: "15px",
        }}
      >
        Exposure Options
      </div>
      <Row
        style={{
          backgroundColor: "#E0E0E0",
        }}
      >
        <DefaultImagingSettingTextBox
          value={autoExposureAlgorithmProperty.minExposure}
          onChange={(name, value) => {
            onChange({
              ...exposureCaptureProperty,
              autoExposure: {
                ...autoExposureAlgorithmProperty,
                minExposure: value,
              },
            });
          }}
          label="Min Exposure"
          name="minExposure"
          unit="ms"
          min={lightpathCapability.camera.exposure.minExposure}
          max={autoExposureAlgorithmProperty.maxExposure - 0.1}
          isInteger={false}
          disabled={false}
          style={{
            gridArea: "minExposure",
          }}
          onErrorOccur={(name) => onErrorOccur(name)}
          onErrorCleared={(name) => onErrorCleared(name)}
        />

        <DefaultImagingSettingTextBox
          value={autoExposureAlgorithmProperty.maxExposure}
          onChange={(name, value) => {
            onChange({
              ...exposureCaptureProperty,
              autoExposure: {
                ...autoExposureAlgorithmProperty,
                maxExposure: value,
              },
            });
          }}
          label="MaxExposure"
          name="maxExposure"
          unit="ms"
          min={autoExposureAlgorithmProperty.minExposure + 0.1}
          max={lightpathCapability.camera.exposure.maxExposure}
          isInteger={false}
          disabled={false}
          style={{
            gridArea: "maxExposure",
          }}
          onErrorOccur={(name) => onErrorOccur(name)}
          onErrorCleared={(name) => onErrorCleared(name)}
        />
      </Row>

      <DefaultImagingSettingRadioButton
        name={AUTO_EXPOSURE_METHOD}
        label="Fixed Exposure"
        checked={
          autoExposureAlgorithmProperty.autoExposureMethod ===
          EAutoExposureMethod.Fixed
        }
        onChange={(name, value) => {
          onChange({
            ...exposureCaptureProperty,
            autoExposure: {
              ...autoExposureAlgorithmProperty,
              autoExposureMethod: EAutoExposureMethod.Fixed,
            },
          });
        }}
        style={{
          marginLeft: "17px",
        }}
        disabled={false}
      />

      {autoExposureAlgorithmProperty.autoExposureMethod ===
        EAutoExposureMethod.Fixed && (
        <Row
          style={{
            backgroundColor: "#E0E0E0",
          }}
        >
          <DefaultImagingSettingTextBox
            value={exposureCaptureProperty.exposure}
            onChange={(name, value) => {
              onChange({
                ...exposureCaptureProperty,
                exposure: value,
              });
            }}
            label="Exposure"
            name="exposure"
            unit="ms"
            min={autoExposureAlgorithmProperty.minExposure}
            max={autoExposureAlgorithmProperty.maxExposure}
            isInteger={false}
            disabled={false}
            style={{
              gridArea: "exposure",
            }}
            onErrorOccur={(name) => onErrorOccur(name)}
            onErrorCleared={(name) => onErrorCleared(name)}
          />

          {lightpathCapability.optic.brightness && (
            <DefaultImagingSettingTextBox
              value={exposureCaptureProperty.brightness}
              onChange={(name, value) => {
                onChange({
                  ...exposureCaptureProperty,
                  brightness: value,
                });
              }}
              label="Brightness"
              name="brightness"
              unit="%"
              min={lightpathCapability.optic.brightness.minBrightness}
              max={lightpathCapability.optic.brightness.maxBrightness}
              isInteger={false}
              disabled={false}
              style={{
                gridArea: "brightness",
              }}
              onErrorOccur={(name) => onErrorOccur(name)}
              onErrorCleared={(name) => onErrorCleared(name)}
            />
          )}
        </Row>
      )}

      <DefaultImagingSettingRadioButton
        name={AUTO_EXPOSURE_METHOD}
        label="Auto Exposure From First Drop"
        checked={
          autoExposureAlgorithmProperty.autoExposureMethod ===
          EAutoExposureMethod.Plate
        }
        onChange={(name, value) => {
          onChange({
            ...exposureCaptureProperty,
            autoExposure: {
              ...autoExposureAlgorithmProperty,
              autoExposureMethod: EAutoExposureMethod.Plate,
            },
          });
        }}
        disabled={false}
        style={{
          marginLeft: "17px",
        }}
      />

      <DefaultImagingSettingRadioButton
        name={AUTO_EXPOSURE_METHOD}
        label="Auto Exposure For Every Drop"
        checked={
          autoExposureAlgorithmProperty.autoExposureMethod ===
          EAutoExposureMethod.Drop
        }
        onChange={(name, value) => {
          onChange({
            ...exposureCaptureProperty,
            autoExposure: {
              ...autoExposureAlgorithmProperty,
              autoExposureMethod: EAutoExposureMethod.Drop,
            },
          });
        }}
        disabled={false}
        style={{
          marginLeft: "17px",
        }}
      />
    </div>
  );
}
